/**
 * The one way this app talks back to the user.
 *
 * Before this existed every module reached for antd's `message` directly,
 * so feedback landed in a different place (top-centre, over the content),
 * looked different per screen, and had no shared duration or wording rules.
 * Everything now goes through `toast` — a single top-right stack with one
 * look, one position and one set of timings.
 *
 *   import toast from "@comps/toast";
 *   toast.success("Case taken successfully");
 *   toast.error(res?.msg || "Something went wrong");
 *   toast.success("Settings saved", "Your changes are live for everyone.");
 *
 * Rendering is Sonner rather than antd's notification: it stacks, it
 * collapses to a deck when several land at once, and a toast can be
 * swiped away. The public API here is unchanged, so the ~40 call sites
 * did not have to move.
 *
 * Mount <AppToaster /> once, at the app root. Styling lives in toast.css;
 * geometry and colour come from theme/tokens.css.
 */

import { Toaster, toast as sonner } from "sonner";
import { LoadingOutlined } from "@ant-design/icons";
import { Icon } from "@iconify/react";
import { TOPBAR_HEIGHT } from "@theme/constants";
import "./toast.css";

/* Sits just clear of the fixed topbar so a toast never covers the header. */
const TOP_OFFSET = TOPBAR_HEIGHT + 16;

/* Errors linger — they usually need reading. Everything else is a receipt. */
const DURATION = {
  success: 3500,
  info: 3500,
  warning: 4500,
  error: 6000,
  loading: Infinity,
};

/*
 * A plain glyph on a solid accent chip. A filled-circle icon inside a
 * tinted circular chip put two circles on top of each other, which read
 * as a dark blob rather than a green tick.
 */
const ICON = {
  success: <Icon icon="tabler:check" width={ 15 } />,
  error: <Icon icon="tabler:x" width={ 15 } />,
  warning: <Icon icon="tabler:alert-triangle" width={ 14 } />,
  info: <Icon icon="tabler:info-circle" width={ 15 } />,
  loading: <LoadingOutlined spin />,
};

/** Mount once at the app root. */
export const AppToaster = () => (
  <Toaster
    position="top-right"
    offset={ TOP_OFFSET }
    visibleToasts={ 3 }
    gap={ 10 }
    closeButton
    // Sonner's own theme is dropped entirely — every surface in this app
    // is painted from tokens.css, and a second palette would drift.
    toastOptions={ { unstyled: true, className: "app-toast" } }
  />
);

const show = (type, title, description, options = {}) => {
  const emit = sonner[type] || sonner;

  return emit(title, {
    description: description || undefined,
    duration: options.duration !== undefined ? options.duration : DURATION[type],
    id: options.key,
    className: `app-toast app-toast--${type}`,
    icon: <span className="app-toast__chip">{ ICON[type] }</span>,
    onClick: options.onClick,
  });
};

const toast = {
  success: (title, description, options) =>
    show("success", title, description, options),
  error: (title, description, options) =>
    show("error", title, description, options),
  warning: (title, description, options) =>
    show("warning", title, description, options),
  info: (title, description, options) =>
    show("info", title, description, options),
  /** Stays until you close it by key — pair with toast.close(key). */
  loading: (title, description, options) =>
    show("loading", title, description, options),
  close: (key) => sonner.dismiss(key),
  closeAll: () => sonner.dismiss(),
};

export default toast;
